import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ProblemSummary } from '@flarecode/shared';
import { api } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * Homepage sections (Tier 4A):
 *   1. Hero — tagline + CTAs (personalized for signed-in users)
 *   2. Live site stats strip
 *   3. Featured problems grid
 *   4. "How it works" 3-step explainer
 *   5. "Built on Cloudflare" tech section
 *
 * Footer is rendered once at the App shell level, not here.
 */

const FEATURED_SLUGS = [
  'two-sum',
  'valid-palindrome',
  'contains-duplicate',
  'best-time-to-buy-and-sell-stock',
  'maximum-subarray',
  'climbing-stairs',
] as const;

export function HomePage() {
  const { user } = useCurrentUser();

  return (
    <div className="home">
      <Hero currentUsername={user?.username} />
      <StatsStrip />
      <FeaturedProblems />
      <HowItWorks />
      <TechSection />
    </div>
  );
}

// ---------- hero ----------

function Hero({ currentUsername }: { currentUsername?: string }) {
  return (
    <section className="home-hero">
      <div className="home-hero-inner">
        <h1 className="home-hero-title">FlareCode</h1>
        <p className="home-hero-tagline">Practice coding problems at the edge.</p>
        {currentUsername ? (
          <SignedInGreeting username={currentUsername} />
        ) : (
          <div className="home-hero-ctas">
            <Link to="/problems" className="cta cta--primary">
              Browse problems →
            </Link>
            <Link to="/register" className="cta cta--secondary">
              Create account
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function SignedInGreeting({ username }: { username: string }) {
  // Reuse the profile endpoint to surface solved count + streak — saves us
  // from adding a dedicated /me/home-stats endpoint. Fast and TanStack-cached.
  const { data } = useQuery({
    queryKey: ['profile', username.toLowerCase()],
    queryFn: () => api.getProfile(username),
  });

  const solved = data?.stats.totalSolved;
  const streak = data?.stats.currentStreakDays;

  return (
    <div className="home-hero-signedin">
      <p className="home-hero-welcome">
        Welcome back, <strong>@{username}</strong>
      </p>
      {data && (
        <p className="home-hero-metrics">
          {solved ?? 0} solved
          {typeof streak === 'number' && streak > 0 && (
            <>
              {' · '}
              {streak}-day streak
            </>
          )}
        </p>
      )}
      <div className="home-hero-ctas">
        <Link to="/problems" className="cta cta--primary">
          Continue solving →
        </Link>
        <Link to={`/u/${username}`} className="cta cta--secondary">
          Your profile
        </Link>
      </div>
    </div>
  );
}

// ---------- live stats strip ----------

function StatsStrip() {
  const { data } = useQuery({
    queryKey: ['siteStats'],
    queryFn: () => api.getSiteStats(),
    staleTime: 60_000,
  });

  return (
    <section className="home-stats">
      <div className="home-stats-row">
        <StatCell value={data?.totalProblems} label="problems" />
        <StatCell value={5} label="languages" note="Py · JS · TS · more soon" />
        <StatCell
          value={data?.totalSubmissions}
          label="submissions"
          placeholderIfZero="just getting started"
        />
        <StatCell value="~1s" label="warm-path latency" note="per submission, on edge" />
      </div>
    </section>
  );
}

function StatCell({
  value,
  label,
  note,
  placeholderIfZero,
}: {
  value: number | string | undefined;
  label: string;
  note?: string;
  placeholderIfZero?: string;
}) {
  const isLoading = value === undefined;
  const isZeroWithPlaceholder = value === 0 && placeholderIfZero;
  return (
    <div className="home-stat">
      <div className="home-stat-value">
        {isLoading ? '—' : isZeroWithPlaceholder ? placeholderIfZero : formatStatValue(value)}
      </div>
      <div className="home-stat-label">{label}</div>
      {note && !isZeroWithPlaceholder && <div className="home-stat-note">{note}</div>}
    </div>
  );
}

function formatStatValue(v: number | string): string {
  if (typeof v === 'string') return v;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
}

// ---------- featured problems grid ----------

function FeaturedProblems() {
  const { data, isLoading } = useQuery({
    queryKey: ['problems'],
    queryFn: () => api.listProblems(),
  });

  const problems = data?.problems ?? [];
  // Preserve the hand-curated order from FEATURED_SLUGS rather than falling
  // back to the list's own ordering.
  const byslug = new Map<string, ProblemSummary>();
  for (const p of problems) byslug.set(p.slug, p);
  const featured = FEATURED_SLUGS.map((s) => byslug.get(s)).filter((p): p is ProblemSummary => !!p);

  if (isLoading || featured.length === 0) return null;

  return (
    <section className="home-featured">
      <h2 className="home-section-title">Try one of these</h2>
      <div className="home-featured-grid">
        {featured.map((p) => (
          <Link key={p.id} to={`/problems/${p.slug}`} className="home-featured-card">
            <div className="home-featured-card-title">{p.title}</div>
            <div className="home-featured-card-meta">
              <span className={`difficulty-chip difficulty--${p.difficulty}`}>{p.difficulty}</span>
              {p.tags.slice(0, 2).map((t) => (
                <span key={t} className="tag-chip">
                  {t}
                </span>
              ))}
            </div>
          </Link>
        ))}
      </div>
      <div className="home-featured-more">
        <Link to="/problems">See all problems →</Link>
      </div>
    </section>
  );
}

// ---------- how it works ----------

function HowItWorks() {
  return (
    <section className="home-hiw">
      <h2 className="home-section-title">How it works</h2>
      <div className="home-hiw-steps">
        <HiwStep
          num={1}
          title="Pick a problem"
          body="Browse the catalog and filter by difficulty, tag, or whether you've solved it yet."
        />
        <HiwStep
          num={2}
          title="Write code"
          body="Monaco editor in your browser. Python, JavaScript, or TypeScript. Your drafts save automatically."
        />
        <HiwStep
          num={3}
          title="Run or Submit"
          body="Run against visible samples for instant feedback, or Submit to run every hidden test case. Results stream live."
        />
      </div>
    </section>
  );
}

function HiwStep({ num, title, body }: { num: number; title: string; body: string }) {
  return (
    <div className="home-hiw-step">
      <div className="home-hiw-num">{num}</div>
      <div className="home-hiw-content">
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </div>
  );
}

// ---------- tech section ----------

function TechSection() {
  return (
    <section className="home-tech">
      <h2 className="home-section-title">Built on Cloudflare</h2>
      <p className="home-tech-body">
        FlareCode runs on Cloudflare Workers, D1, and edge containers. Every submission dispatches
        to a pooled container running near you for sub-second warm-path latency. Judge progress
        streams live over WebSocket via Durable Objects.
      </p>
    </section>
  );
}
